/**
 * Everything that can stop the address screen from continuing.
 *
 * The messages shown beside each field and the check the Next button runs are
 * derived here together, so a field can't fail validation without the user
 * being able to see which one. The three sibling hooks — loading capabilities,
 * deadline, Amazon references — own their own messages and hand them in.
 */

import { locationsErrorMessageForBoothNumber } from "@/Shared/Locations/Validators/locationsErrorMessageForBoothNumber";
import { warningMessageForPickupHours } from "@/Shared/Locations/Validators/locationsErrorMessageForHours";
import { locationsErrorMessageForNotes } from "@/Shared/Locations/Validators/locationsErrorMessageForNotes";
import { errorMessageForSameDeliveryAddress } from "@/Shared/Locations/Validators/locationsErrorMessagesForDeliveryLocation";
import { locationsErrorMessagesForLocation } from "@/Shared/Locations/Validators/locationsErrorMessagesForLocation";
import { getRestrictionViolation } from "@/Shared/Locations/Helpers/locationsHoursOptions";
import {
  EmailRequired,
  contactsErrorMessagesForContact,
  warningMessagesForContact,
} from "@/Shared/Contacts/Validators/contactsErrorMessagesForContact";
import type { AddContactContactType } from "@/Shared/Types/contactTypes";
import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";
import type { useBookShipmentAddressDeadline } from "./useBookShipmentAddressDeadline";
import type { useBookShipmentAddressLoadingCapabilities } from "./useBookShipmentAddressLoadingCapabilities";
import type { useBookShipmentAmazonReference } from "./useBookShipmentAmazonReference";

interface BookShipmentAddressValidationInput {
  context: LocationContext;
  location: PreQuoteLocation;
  shipment: PreBookingShipment;
  contact: Contact;
  referenceNumber: string;
  boothNumber: string;
  isDeliveryAppointment: boolean;
  isContactNotRequired: boolean;
  onModify: () => void;
  loadingCapabilities: ReturnType<
    typeof useBookShipmentAddressLoadingCapabilities
  >;
  deadline: ReturnType<typeof useBookShipmentAddressDeadline>;
  amazon: ReturnType<typeof useBookShipmentAmazonReference>;
}

export function useBookShipmentAddressValidation(
  input: BookShipmentAddressValidationInput,
) {
  const {
    context,
    location,
    shipment,
    contact,
    referenceNumber,
    boothNumber,
    isDeliveryAppointment,
    isContactNotRequired,
    onModify,
    loadingCapabilities,
    deadline,
    amazon,
  } = input;
  const isPickup = context === LocationContext.Pickup;
  const selectedQuote = shipment.selectedQuote;

  const comparisonContacts: Map<string, AddContactContactType> = new Map();
  if (context === LocationContext.Delivery) {
    comparisonContacts.set("pickup", shipment.pickupContact || {});
  }

  // TODO: Second param
  const locationErrors = locationsErrorMessagesForLocation(location, false);

  const contactErrors = contactsErrorMessagesForContact(
    contact,
    EmailRequired.EmailNotRequired,
    comparisonContacts,
  );
  const contactWarnings = warningMessagesForContact(contact, location, context);

  // Pickups only — the message talks about when carriers start pickups, which
  // says nothing about a delivery window.
  const pickupHoursWarnings = isPickup
    ? warningMessageForPickupHours({
        openFrom: location.hours.openFrom,
        openUntil: location.hours.openUntil,
      })
    : undefined;

  const restrictionError =
    selectedQuote?.restrictions && selectedQuote.restrictions.length > 0
      ? getRestrictionViolation(
          isPickup ? location.hours.openFrom : location.hours.openUntil,
          context,
          isPickup ? "openFrom" : "openUntil",
          selectedQuote.restrictions,
          shipment.pickupLocation?.hours?.openUntil,
        )
      : undefined;

  const notesErrorMessage = locationsErrorMessageForNotes(
    location.notes,
    location.accessorials,
    location.locationType,
    context,
    onModify,
  );

  const boothErrorMessage = locationsErrorMessageForBoothNumber(
    boothNumber,
    location.locationType,
  );

  function isReferenceNumberRequired(): boolean {
    return location.locationType === LocationType.DistributionWarehouse;
  }

  function referenceNumberError(): string | undefined {
    if (
      isReferenceNumberRequired() &&
      !amazon.isAmazonDelivery &&
      (!referenceNumber || referenceNumber.trim() === "")
    ) {
      const displayName = isPickup ? "Pickup" : "Delivery";
      return `${displayName} reference number is required for Large Distribution Warehouse`;
    }
    return undefined;
  }

  function sameAddressAsPickupError(): string | undefined {
    if (context !== LocationContext.Delivery) return undefined;
    return errorMessageForSameDeliveryAddress(
      shipment.pickupLocation.address,
      location.address,
    );
  }

  function isScreenCompletelyEntered() {
    if (loadingCapabilities.needsLiftGate()) {
      console.error(
        "[BookShipmentAddressScreenComponent] Validation failed: location needs lift gate",
      );
      return false;
    }

    // Only include contact errors if contact is required for this location
    // This prevents stale contact validation from causing hidden errors
    const validationResults = {
      businessName: locationErrors.businessName,
      addressLine:
        locationErrors.address.addressLine ?? sameAddressAsPickupError(),
      hours: isDeliveryAppointment ? undefined : locationErrors.hours,
      contactEmail: isContactNotRequired
        ? undefined
        : contactErrors.emailAddress,
      contactName: isContactNotRequired ? undefined : contactErrors.contactName,
      contactPhone: isContactNotRequired
        ? undefined
        : contactErrors.phoneNumber,
      notes: notesErrorMessage,
      booth: boothErrorMessage,
      deadline: deadline.deadlineErrors,
      hasDeadline: deadline.hasDeadlineErrors,
      amazonReferenceId: amazon.amazonReferenceIdError(),
      amazonShipmentId: amazon.amazonShipmentIdError(),
      referenceNumber: referenceNumberError(),
      locationHasDock: loadingCapabilities.dockError(),
      locationHasForklift: loadingCapabilities.forkliftError(),
      restriction: restrictionError,
    };

    const failedValidations = Object.entries(validationResults).filter(
      ([, error]) => error !== undefined,
    );

    if (failedValidations.length > 0) {
      console.error(
        "[BookShipmentAddressScreenComponent] Validation failures:",
        Object.fromEntries(failedValidations),
      );
    }

    return failedValidations.length === 0;
  }

  return {
    locationErrors,
    contactErrors,
    contactWarnings,
    pickupHoursWarnings,
    restrictionError,
    notesErrorMessage,
    boothErrorMessage,
    isReferenceNumberRequired,
    referenceNumberError,
    sameAddressAsPickupError,
    isScreenCompletelyEntered,
  };
}
